import {
  Box,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import type {
  PremiseConfig,
  PremiseHealth,
  PremiseScreen,
  VideoCallSeatSummary,
} from "@shared/types/foyer";
import {
  DEFAULT_METERED_TARGET,
  isCarePremise,
  resolveCallPolicy,
  type MeteredTarget,
} from "@shared/types/foyer";
import PeopleAltIcon from "@mui/icons-material/PeopleAltOutlined";
import TvIcon from "@mui/icons-material/TvOutlined";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKeyOutlined";
import { foyerApi, type LicenseSeatSummary } from "../http/foyer";
import { KpiCard } from "../components/KpiCard";
import HealthChip from "../components/HealthChip";
import { vendorFor } from "../utils/devicePresentation";
import { tokens } from "../theme";
import OnlineScreensDonut from "../components/OnlineScreensDonut";
import PremiseLocationCard from "../components/PremiseLocationCard";
import { useAuth } from "../auth/AuthContext";

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

interface EnrichedScreen extends PremiseScreen {
  live?: { lastSeenAt?: string };
}

/**
 * Per-premise dashboard. Cards mirror the Nenda admin layout the user
 * shared: Key data, Online Screens donut, Displays, Location/Map,
 * Licenses. A users-with-access card is still a possible addition;
 * user/role/invite management lives on the global Users page today.
 *
 * The page reads `premise` straight from the editor draft so any save
 * is reflected immediately without an extra fetch. The Devices info
 * (online + offline counts, vendor/model rollups) needs the live
 * heartbeat snapshot, so we fetch /api/foyer/premise/:id/screens once
 * on mount + every 30s.
 */
export default function PremiseOverviewPage({ premise, onChange }: Props) {
  const { user } = useAuth();
  const [enriched, setEnriched] = useState<EnrichedScreen[]>([]);
  // Live occupancy + license seats for the at-a-glance strip. Seeded from
  // the editor draft (premise.rooms) for first paint; refreshed on the
  // same 30s cadence as the screen telemetry.
  const [liveRooms, setLiveRooms] = useState<
    { id: string; occupied?: boolean }[] | null
  >(null);
  const [license, setLicense] = useState<LicenseSeatSummary | null>(null);
  const [health, setHealth] = useState<PremiseHealth | null>(null);
  const [seats, setSeats] = useState<VideoCallSeatSummary | null>(null);

  // Video-call seats are a second, unrelated entitlement (residents, not
  // screens) that only exists on a care premise with calling switched on -
  // so the strip only grows a fourth card when both are true, and we only
  // spend the request then.
  const callingEnabled =
    isCarePremise(premise) && resolveCallPolicy(premise.care).enabled;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [list, rooms, lic, hp, vc] = await Promise.all([
          foyerApi.listScreens(premise.premiseId) as Promise<EnrichedScreen[]>,
          foyerApi.listRooms(premise.premiseId).catch(() => null),
          foyerApi.licenseSummary(premise.premiseId).catch(() => null),
          foyerApi.premiseHealth(premise.premiseId).catch(() => null),
          callingEnabled
            ? foyerApi.videoCallSeatSummary(premise.premiseId).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (Array.isArray(list)) setEnriched(list);
        if (Array.isArray(rooms)) setLiveRooms(rooms);
        if (lic && typeof lic.totalSeats === "number") setLicense(lic);
        if (hp && typeof hp.status === "string") setHealth(hp);
        setSeats(vc && typeof vc.poolSeats === "number" ? vc : null);
      } catch {
        // Not fatal - the static `premise.screens` data still drives
        // the displays card below.
      }
    }
    load();
    const t = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [premise.premiseId, callingEnabled]);

  const allScreens = premise.screens ?? [];
  const onlineCount = enriched.filter((s) =>
    isOnline(s.live?.lastSeenAt ?? s.lastSeenAt)
  ).length;

  // Metered usage (2026-09-14): a commercial fact about the ad-rotation
  // contract, not an operator setting - so it's Nenda-admin-only, same
  // posture as the global signage channels. Gated on the premise actually
  // HAVING a signage screen, because the flag is meaningless otherwise (and
  // showing it everywhere would be the "a complete backend nobody can reach"
  // shape this codebase has grown before, just in reverse - a switch with no
  // number behind it).
  const hasSignageScreen = allScreens.some((s) => s.role === "signage");
  const canToggleMeteredUsage = user?.type === "admin" && hasSignageScreen;

  // ── At-a-glance stats ────────────────────────────────────────────────
  // `?? []` is not paranoia about the type - it is about the blast radius. A
  // config that arrives without `rooms` (a truncated response, a half-written
  // fixture) would otherwise throw during render and take the ENTIRE CMS to a
  // blank page, rather than showing this one card with a zero in it.
  const roomsForStats = liveRooms ?? premise.rooms ?? [];
  const totalRooms = roomsForStats.length;
  const occupiedRooms = roomsForStats.filter((r) => r.occupied).length;
  const occupancyPct = totalRooms
    ? Math.round((occupiedRooms / totalRooms) * 100)
    : 0;

  const licenseFull = !!license && !license.unlimited && !license.hasFreeSeats;
  const licenseValue = license
    ? license.unlimited
      ? `${license.activatedCount}`
      : `${license.activatedCount} / ${license.totalSeats}`
    : "-";
  const licenseSub = license
    ? license.unlimited
      ? "Unlimited seats"
      : license.hasFreeSeats
        ? `${license.totalSeats - license.activatedCount} seat${
            license.totalSeats - license.activatedCount === 1 ? "" : "s"
          } free`
        : "Seat cap reached"
    : "Loading…";

  // Video calling: the pool the premise bought, how much of it is handed out,
  // and - counted apart, because it sits ON TOP of the pool and nobody here
  // pays for it - what relatives bought themselves in the family portal.
  const seatsFull = !!seats && !seats.unlimited && !seats.hasFreeSeats;
  const seatsValue = seats
    ? seats.unlimited
      ? String(seats.assignedFromPool)
      : `${seats.assignedFromPool} / ${seats.poolSeats}`
    : "-";

  // Vendor + model rollups for the Displays card.
  const modelCounts = new Map<string, number>();
  const vendorCounts = new Map<string, number>();
  for (const s of allScreens) {
    modelCounts.set(s.model, (modelCounts.get(s.model) ?? 0) + 1);
    const vendor = vendorFor(s.model);
    vendorCounts.set(vendor, (vendorCounts.get(vendor) ?? 0) + 1);
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        sx={{ alignItems: "stretch", flexWrap: "wrap", gap: 3 }}
      >
        <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
          <KpiCard
            label="Occupancy"
            value={`${occupiedRooms} / ${totalRooms}`}
            sub={`${occupancyPct}% of rooms occupied`}
            icon={<PeopleAltIcon />}
          />
        </Box>
        <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
          <KpiCard
            label="Screens"
            value={String(allScreens.length)}
            sub={`${onlineCount} online`}
            icon={<TvIcon />}
          />
        </Box>
        <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
          <KpiCard
            label="Licenses"
            value={licenseValue}
            sub={licenseSub}
            icon={<VpnKeyIcon />}
            tint={licenseFull ? tokens.warningTint : tokens.brandTint}
            accent={licenseFull ? tokens.warning : tokens.brand}
          />
        </Box>
        {callingEnabled && (
          <Box
            sx={{ flex: "1 1 220px", minWidth: 220 }}
            data-testid="overview-video-call-seats"
          >
            <KpiCard
              label="Video calling seats"
              value={seatsValue}
              sub={<SeatSub seats={seats} />}
              icon={<VideocamIcon />}
              tint={seatsFull ? tokens.warningTint : tokens.brandTint}
              accent={seatsFull ? tokens.warning : tokens.brand}
            />
          </Box>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={3}
        sx={{ alignItems: "stretch", flexWrap: "wrap" }}
      >
        <KeyDataCard premise={premise} screens={allScreens} />
        <ScreensCard online={onlineCount} total={allScreens.length} />
        <HealthCard health={health} />
      </Stack>
      <Stack
        direction="row"
        spacing={3}
        sx={{ alignItems: "stretch", flexWrap: "wrap" }}
      >
        <DisplaysCard
          vendors={[...vendorCounts.entries()]}
          models={[...modelCounts.entries()]}
        />
        <PremiseLocationCard premise={premise} onChange={onChange} />
        {canToggleMeteredUsage && (
          <MeteredUsageCard premise={premise} onChange={onChange} />
        )}
      </Stack>
    </Stack>
  );
}

/**
 * The "Metered usage" flag and target (2026-09-14, target 2026-09-23): does
 * this premise's ad-rotation contract oblige its signage screen(s) to run,
 * and how much. Nenda-admin-only (see `canToggleMeteredUsage` above). The
 * target is either a fixed number of hours per month or a share of the
 * premise's open hours; both numbers are kept so flipping the mode never
 * loses the other. Drives the pacing dashboard and the monthly KPI under
 * Fleet health.
 */
function MeteredUsageCard({
  premise,
  onChange,
}: {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}) {
  const target: MeteredTarget = premise.meteredTarget ?? DEFAULT_METERED_TARGET;
  const enabled = premise.meteredUsage === true;
  const setTarget = (patch: Partial<MeteredTarget>) =>
    onChange({ ...premise, meteredTarget: { ...target, ...patch } });
  // Keep what the user is typing (including an empty field mid-edit) and
  // only push a real positive number up; the server clamps it regardless.
  const [draft, setDraft] = useState<string | null>(null);
  const value = target.mode === "hours" ? target.hours : target.uptimePercent;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, flex: "1 1 260px", minWidth: 260, borderRadius: "12px" }}
      data-testid="metered-usage-card"
    >
      <Stack spacing={1.5}>
        <Typography sx={{ fontWeight: 600 }}>Metered usage</Typography>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          Contractually obliged to keep signage on. Only playback inside the
          premise&apos;s opening hours counts. Feeds the pacing dashboard and
          the monthly KPI under Fleet health.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) =>
                onChange({ ...premise, meteredUsage: e.target.checked })
              }
              inputProps={{
                "aria-label": "Metered usage",
              }}
              data-testid="metered-usage-toggle"
            />
          }
          label={enabled ? "Metered" : "Not metered"}
        />
        {enabled && (
          <Stack spacing={1.5}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={target.mode}
              onChange={(_e, mode: MeteredTarget["mode"] | null) => {
                if (!mode) return;
                setDraft(null);
                setTarget({ mode });
              }}
              aria-label="Metered target type"
              data-testid="metered-target-mode"
            >
              <ToggleButton value="hours">Hours / month</ToggleButton>
              <ToggleButton value="uptime">Uptime %</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              type="number"
              label={
                target.mode === "hours"
                  ? "Hours per month"
                  : "Share of open hours (%)"
              }
              value={draft ?? String(value)}
              onChange={(e) => {
                setDraft(e.target.value);
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n > 0) {
                  setTarget(
                    target.mode === "hours"
                      ? { hours: n }
                      : { uptimePercent: n }
                  );
                }
              }}
              onBlur={() => setDraft(null)}
              helperText={
                target.mode === "hours"
                  ? "Playing hours owed each calendar month."
                  : "Owed share of the premise's open hours each month - a premise open longer owes more."
              }
              inputProps={{
                min: 1,
                max: target.mode === "hours" ? 744 : 100,
                "data-testid": "metered-target-value",
              }}
              sx={{ maxWidth: 260 }}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * The footer line of the video-calling KPI: what is left of the premise's own
 * pool, then - kept as its own figure - the seats relatives bought in the
 * family portal.
 *
 * They are shown apart rather than summed on purpose: a family seat is paid
 * for by the relative and never draws on the pool (`assignedFromPool` is what
 * the cap counts), so folding the two together would tell an operator they
 * have fewer seats to give away than they do.
 */
function SeatSub({ seats }: { seats: VideoCallSeatSummary | null }) {
  if (!seats) return <>Loading…</>;
  return (
    <>
      {seats.unlimited ? "Site license" : `${seats.freeSeats} free`}
      {" · "}
      <Tooltip title="Bought by relatives in the family portal - paid separately, so they come on top of this site's seats and never use them up.">
        <Box
          component="span"
          sx={{ fontWeight: 600, textDecoration: "underline dotted" }}
          data-testid="overview-video-call-family"
        >
          +{seats.familySeats} family-funded
        </Box>
      </Tooltip>
    </>
  );
}

function KeyDataCard({
  premise,
  screens,
}: {
  premise: PremiseConfig;
  screens: PremiseScreen[];
}) {
  const rows: Array<[string, React.ReactNode]> = [
    ["ID", premise.premiseId],
    ["Version", `v${premise.version}`],
    ["Rooms", String(premise.rooms.length)],
    ["TVs", String(screens.length)],
    ["TimeZone", premise.address?.timezone ?? "-"],
    ["Updated", premise.updatedAt ? fmtDate(premise.updatedAt) : "-"],
    ["Created", premise.createdAt ? fmtDate(premise.createdAt) : "-"],
  ];
  return (
    <Card title="Key data" sx={{ flex: "1 1 360px", minWidth: 320 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          rowGap: 1,
          columnGap: 3,
          fontSize: 14,
        }}
      >
        {rows.map(([k, v]) => (
          <Box key={k} sx={{ display: "contents" }}>
            <Box sx={{ fontWeight: 600, color: "text.secondary" }}>{k}</Box>
            <Box sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{v}</Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

function ScreensCard({ online, total }: { online: number; total: number }) {
  return (
    <Card
      title=""
      sx={{
        flex: "1 1 280px",
        minWidth: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <OnlineScreensDonut online={online} total={total} />
    </Card>
  );
}

/**
 * At-a-glance device-health card: the traffic-light chip plus the underlying
 * breakdown (online/offline, sustained-offline, never-reported, version drift,
 * recent failed commands). Mirrors the rollup the premise list chip is built on.
 */
function HealthCard({ health }: { health: PremiseHealth | null }) {
  const rows: Array<[string, string, boolean]> = health
    ? [
        ["Online", `${health.online} / ${health.deviceCount}`, false],
        ["Offline", String(health.offline), health.offline > 0],
        [
          `Offline >${health.staleThresholdMinutes}m`,
          String(health.stale),
          health.stale > 0,
        ],
        [
          "Never reported",
          String(health.neverReported),
          health.neverReported > 0,
        ],
        ["Version drift", String(health.versionDrift), health.versionDrift > 0],
        [
          "Failed cmds (recent)",
          String(health.failedCommands),
          health.failedCommands > 0,
        ],
        [
          `Weak Wi-Fi (<${health.wifiPoorDbm} dBm)`,
          String(health.weakWifi),
          health.weakWifi >= 2,
        ],
        [
          `Critical Wi-Fi (≤${health.wifiCriticalDbm} dBm)`,
          String(health.criticalWifi),
          health.criticalWifi > 0,
        ],
      ]
    : [];
  return (
    <Card title="" sx={{ flex: "1 1 280px", minWidth: 280 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h6">Device health</Typography>
          <HealthChip health={health ?? undefined} testId="overview-health" />
        </Stack>
        {!health ? (
          <Typography variant="caption" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr max-content",
              rowGap: 0.75,
              columnGap: 3,
              fontSize: 14,
            }}
          >
            {rows.map(([k, v, warn]) => (
              <Box key={k} sx={{ display: "contents" }}>
                <Box sx={{ color: "text.secondary" }}>{k}</Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    textAlign: "right",
                    color: warn ? tokens.error : "text.primary",
                  }}
                >
                  {v}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {health?.latestVersion && (
          <Typography variant="caption" color="text.secondary">
            Prevalent version:{" "}
            <Box
              component="span"
              sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            >
              {health.latestVersion}
            </Box>
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

function DisplaysCard({
  vendors,
  models,
}: {
  vendors: Array<[string, number]>;
  models: Array<[string, number]>;
}) {
  return (
    <Card title="Displays" sx={{ flex: "1 1 360px", minWidth: 320 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Vendors
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }}>
            {vendors.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No devices paired.
              </Typography>
            )}
            {vendors.map(([v, n]) => (
              <Chip key={v} label={`${v}: ${n}`} size="small" />
            ))}
          </Stack>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Models
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }}>
            {models.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                -
              </Typography>
            )}
            {models.map(([m, n]) => (
              <Chip
                key={m}
                label={`${m}: ${n}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}

/** Light Paper wrapper with a section title. */
function Card({
  title,
  children,
  sx,
}: {
  title: string;
  children: React.ReactNode;
  sx?: React.ComponentProps<typeof Paper>["sx"];
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, ...sx }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

/** Online if last heartbeat within 60s - matches server-side window. */
function isOnline(lastSeenAtIso: string | undefined): boolean {
  if (!lastSeenAtIso) return false;
  const t = Date.parse(lastSeenAtIso);
  return Number.isFinite(t) && Date.now() - t < 60_000;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
